import { openDB, IDBPDatabase } from 'idb'
import {
    decryptPayload,
    encryptPayload,
    isLegacyUnownedRow,
    maySyncRow,
    type OfflineOwner,
    type OfflineEnvelopeV1,
} from './ownerEnvelope'

const DB_NAME = 'asc-compliance-offline'
const DB_VERSION = 2
const STORE_NAME = 'drafts'

/**
 * Offline Store [FE.2.1] — Wave 1 / #1861 F-04 owner-bound storage.
 * PHI-capable drafts require an active owner; legacy unowned rows are purged.
 */
class OfflineStore {
    private db: Promise<IDBPDatabase> | null
    private activeOwner: OfflineOwner | null = null
    private sessionMaterial: string | null = null

    constructor() {
        if (typeof indexedDB === 'undefined') {
            this.db = null
            return
        }
        this.db = openDB(DB_NAME, DB_VERSION, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id' })
                }
            },
        })
    }

    /** Bind offline I/O to the authenticated identity. Required before save/read of PHI drafts. */
    setActiveOwner(owner: OfflineOwner | null, sessionMaterial?: string | null) {
        this.activeOwner = owner
        this.sessionMaterial = sessionMaterial ?? null
    }

    getActiveOwner(): OfflineOwner | null {
        return this.activeOwner
    }

    /**
     * Save a draft locally with owner envelope + AES-GCM when WebCrypto is available.
     */
    async saveDraft(id: string, data: unknown) {
        if (!this.db) return
        if (!this.activeOwner || !this.sessionMaterial) {
            throw new Error('offline_owner_required')
        }
        const db = await this.db
        const envelope = await encryptPayload(this.activeOwner, this.sessionMaterial, data)
        const payload = {
            id,
            timestamp: new Date().toISOString(),
            owner_id: this.activeOwner.userId,
            owner_user_id: this.activeOwner.userId,
            owner_tenant_id: this.activeOwner.tenantId,
            envelope,
            // content intentionally omitted when encrypted
        }
        await db.put(STORE_NAME, payload)
    }

    async getDraft(id: string) {
        if (!this.db) return undefined
        if (!this.activeOwner) {
            throw new Error('offline_owner_required')
        }
        const db = await this.db
        const row = await db.get(STORE_NAME, id)
        if (!row) return undefined
        if (isLegacyUnownedRow(row as Record<string, unknown>)) {
            await db.delete(STORE_NAME, id)
            return undefined
        }
        const rowOwner: OfflineOwner = {
            userId: String(row.owner_user_id || row.owner_id),
            tenantId: String(row.owner_tenant_id || ''),
        }
        if (!maySyncRow(rowOwner, this.activeOwner)) {
            throw new Error('offline_owner_mismatch')
        }
        return row
    }

    /**
     * Read + decrypt draft content for the active owner.
     * Returns undefined when missing, legacy-purged, or decrypt fails.
     * Throws offline_owner_mismatch / offline_owner_required on identity errors.
     */
    async getDraftContent(id: string): Promise<unknown | undefined> {
        if (!this.db) return undefined
        if (!this.activeOwner || !this.sessionMaterial) {
            throw new Error('offline_owner_required')
        }
        const row = await this.getDraft(id)
        if (!row) return undefined
        if (row.envelope && this.sessionMaterial) {
            try {
                return await decryptPayload(
                    row.envelope as OfflineEnvelopeV1,
                    this.activeOwner,
                    this.sessionMaterial,
                )
            } catch (err) {
                if (err instanceof Error && err.message === 'offline_owner_mismatch') throw err
                await this.removeDraft(id)
                return undefined
            }
        }
        if (row.content != null) return row.content
        return undefined
    }

    async getAllDrafts() {
        if (!this.db) return []
        // F-04: refuse read/sync until an authenticated owner is bound
        if (!this.activeOwner || !this.sessionMaterial) return []
        const db = await this.db
        const all = await db.getAll(STORE_NAME)
        const kept: Array<Record<string, unknown>> = []
        for (const row of all) {
            if (isLegacyUnownedRow(row as Record<string, unknown>)) {
                await db.delete(STORE_NAME, row.id)
                continue
            }
            const rowOwner: OfflineOwner = {
                userId: String(row.owner_user_id || row.owner_id),
                tenantId: String(row.owner_tenant_id || ''),
            }
            if (!maySyncRow(rowOwner, this.activeOwner)) {
                continue
            }
            // Hydrate plaintext content for sync only after owner match + decrypt
            if (row.envelope && this.sessionMaterial) {
                try {
                    const content = await decryptPayload(
                        row.envelope as OfflineEnvelopeV1,
                        this.activeOwner,
                        this.sessionMaterial,
                    )
                    kept.push({ ...row, content })
                } catch {
                    // Corrupt or wrong key — quarantine by delete
                    await db.delete(STORE_NAME, row.id)
                }
            } else if (row.content != null) {
                // Transitional: owned plaintext rows (pre-encrypt migrate)
                kept.push(row)
            }
        }
        return kept
    }

    async purgeLegacyUnowned(): Promise<number> {
        if (!this.db) return 0
        const db = await this.db
        const all = await db.getAll(STORE_NAME)
        let n = 0
        for (const row of all) {
            if (isLegacyUnownedRow(row as Record<string, unknown>)) {
                await db.delete(STORE_NAME, row.id)
                n++
            }
        }
        return n
    }

    async removeDraft(id: string) {
        if (!this.db) return
        const db = await this.db
        await db.delete(STORE_NAME, id)
    }

    /**
     * WIPE RULE: Immediately clear all local data on logout / identity switch.
     */
    async wipeAll() {
        if (!this.db) return
        const db = await this.db
        const tx = db.transaction(STORE_NAME, 'readwrite')
        await tx.objectStore(STORE_NAME).clear()
        await tx.done
        this.activeOwner = null
        this.sessionMaterial = null
    }
}

export const offlineStore = new OfflineStore()
